import { takeEvery, put, all, select } from "redux-saga/effects";
import { defineMessages } from "react-intl";
import * as AttachmentsActions from "./attachmentsActions";
import { makeApiCall } from "../helpers";
import { ALERT_TYPES, showAlert } from "../alert/alertActions";
import attachmentsServices from "../../services/attachmentServices";

const messages = defineMessages({
    deleteAttachmentSucceeded: {
        id: "alerts.attachments-sagas.delete-attachment-succeeded",
        defaultMessage: "Attachment deleted successfully",
        description: "Attachment deleted successfully alert message",
    },
    deleteAttachmentFailed: {
        id: "alerts.attachments-sagas.delete-attachment-failed",
        defaultMessage: "Deleting attachment failed",
        description: "Delete attachment failed alert message",
    },
    fetchAttachmentsFailed: {
        id: "alerts.attachments-sagas.fetch-attachments-failed",
        defaultMessage: "Fetching attachments failed",
        description: "Fetching attachments failed alert message",
    },
    updateAttachmentFailed: {
        id: "alerts.attachments-sagas.update-attachments-failed",
        defaultMessage: "Updating attachment failed",
        description: "Updating attachments failed alert message",
    },
    updateAttachmentSucceeded: {
        id: "alerts.attachments-sagas.update-attachments-succeeded",
        defaultMessage: "Updating attachments succeeded",
        description: "Uploading attachments succeeded alert message",
    },
    uploadAttachmentsFailed: {
        id: "alerts.attachments-sagas.upload-attachments-failed",
        defaultMessage: "Uploading attachments failed",
        description: "Uploading attachments failed alert message",
    },
    uploadAttachmentsSucceeded: {
        id: "alerts.attachments-sagas.upload-attachments-succeeded",
        defaultMessage: "Uploading attachments succeeded",
        description: "Uploading attachments succeeded alert message",
    },
});

export const watchAttachmentsRequests = function* () {
    yield takeEvery(AttachmentsActions.FETCH_ATTACHMENTS, getAttachments);
    yield takeEvery(AttachmentsActions.FETCH_FOLLOWUP_ATTACHMENTS, getFollowupAttachments);
    yield takeEvery(AttachmentsActions.UPDATE_ATTACHMENTS, updateAttachments);
    yield takeEvery(AttachmentsActions.UPLOAD_ATTACHMENTS, attachmentUpload);
    yield takeEvery(AttachmentsActions.DELETE_ATTACHMENT, deleteAttachment);
    yield takeEvery(AttachmentsActions.FILE_UPLOAD_AUDIT_ACTIVITY, fileUploadAudit);
    yield takeEvery(AttachmentsActions.FILE_UPDATE_AUDIT_ACTIVITY, fileUpdateAudit);
};

const getAttachments = function* (action) {
    try {
        const resp = yield makeApiCall(attachmentsServices.fetchAttachments, action.workOrderNumber);
        yield put(AttachmentsActions.fetchAttachmentsSucceeded(resp));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.fetchAttachmentsFailed, ALERT_TYPES.error, { responseError }));
    }
};

const getFollowupAttachments = function* (action) {
    try {
        const resp = yield makeApiCall(attachmentsServices.fetchAttachments, action.workOrderNumber);
        yield put(AttachmentsActions.fetchFollowupAttachmentsSucceeded(resp, action.workOrderNumber));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.fetchAttachmentsFailed, ALERT_TYPES.error, { responseError }));
    }
};

const attachmentUpload = function* (action) {
    try {
        // Call multiple times
        const resp = yield all(
            action.filesToUpload.map((file) => {
                return makeApiCall(attachmentsServices.uploadAttachment, file);
            }),
        );

        yield put(AttachmentsActions.fileUploadResponse(resp));
        yield put(showAlert(Date.now(), messages.uploadAttachmentsSucceeded, ALERT_TYPES.success));
        yield put(AttachmentsActions.setFilesToUpload([]));

        // Refresh list of files if it was done by the same WO (safe access)
        const relatedSourceId = action.filesToUpload?.[0]?.relatedSourceId;
        const currentWorkOrderNumber = yield select((s) => s?.tasks?.workorderData?.workorderData?.workOrderNumber ?? null);
        // Only refresh if the upload was for the current work order and not elsewhere (e.g., activityQuestions)
        if (currentWorkOrderNumber == relatedSourceId) {
            yield put(AttachmentsActions.fetchAttachments(relatedSourceId));
        }
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.uploadAttachmentsFailed, ALERT_TYPES.error, { responseError }));
    }
};

const updateAttachments = function* (action) {
    try {
        const resp = yield all(
            action.filesToUpdate.map((file) => {
                return makeApiCall(attachmentsServices.updateAttachment, file);
            }),
        );

        yield put(AttachmentsActions.fileUpdateResponse(resp));
        // Refresh list of files
        yield put(AttachmentsActions.fetchAttachments(action.filesToUpdate[0].relatedSourceId));

        //yield put(AttachmentsActions.uploadAttachmentsSucceeded(resp));
        yield put(showAlert(Date.now(), messages.updateAttachmentSucceeded, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.updateAttachmentFailed, ALERT_TYPES.error, { responseError }));
    }
};

const deleteAttachment = function* (action) {
    try {
        const resp = yield makeApiCall(attachmentsServices.removeAttachment, action.fileToRemove.id);

        // Refresh list of files
        yield put(AttachmentsActions.fetchAttachments(action.fileToRemove.workOrderNumber));
        yield makeApiCall(attachmentsServices.removeAttachmentAuditActivity, action.fileToRemove.payload);

        //yield put(AttachmentsActions.uploadAttachmentsSucceeded(resp));
        yield put(showAlert(Date.now(), messages.deleteAttachmentSucceeded, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.deleteAttachmentFailed, ALERT_TYPES.error, { responseError }));
    }
};

const fileUploadAudit = function* (action) {
    try {
        const resp = yield makeApiCall(attachmentsServices.fileUploadAuditActivity, action.payload);
    } catch (responseError) {
        console.log(responseError);
    }
};

const fileUpdateAudit = function* (action) {
    try {
        const resp = yield makeApiCall(attachmentsServices.fileUpdateAuditActivity, action.payload);
    } catch (responseError) {
        console.log(responseError);
    }
};
